/**
 * 커스텀 Modal – alert / confirm 대체.
 * 전역에서 사용: Modal.alert(message), Modal.confirm(message).
 * 스타일: resources/scss/admin/components/_app-modal.scss (인라인 스타일 미사용).
 *
 * 중첩 방지: 한 번에 모달 하나만 표시한다. 새 모달을 열기 직전 기존 모달을 닫고(그 promise 는
 * 취소값으로 resolve), 이번 모달만 남긴다 — confirm 이 어떤 경로로든 2회+ 호출돼도 시각적으로
 * 쌓이지 않는다(삭제 확인 모달 중첩 버그 방지).
 */

import { t } from "./I18n";

const CONTAINER_ID = "app-modal-root";
const BACKDROP_CLASS = "app-modal-backdrop";
const BOX_CLASS = "app-modal-box";
const HEADER_CLASS = "app-modal-header";
const TITLE_CLASS = "app-modal-title";
const CLOSE_CLASS = "app-modal-close";
const BODY_CLASS = "app-modal-body";
const FOOTER_CLASS = "app-modal-footer";
const BTN_CLASS = "app-modal-btn";
const BTN_PRIMARY_CLASS = "app-modal-btn--primary";
const STATUS_CLASS = "app-modal-status";

// i18n(현재 로케일) 우선, 미주입 시 영어 폴백. (본 사이트는 영문 기본.)
function getModalLabel(key: "ok" | "cancel" | "close" | "alert_title" | "confirm_title" | "content_title"): string {
  const fallback: Record<string, string> = {
    ok: "OK",
    cancel: "Cancel",
    close: "Close",
    alert_title: "Notice",
    confirm_title: "Confirm",
    content_title: "Notice",
  };
  const translated = t(`modal.${key}`);
  // t() 는 미존재 시 키 문자열을 그대로 돌려주므로, 그 경우 영어 폴백 사용.
  return translated === `modal.${key}` ? (fallback[key] ?? "") : translated;
}

function getContainer(): HTMLDivElement {
  let root = document.getElementById(CONTAINER_ID) as HTMLDivElement | null;
  if (!root) {
    root = document.createElement("div");
    root.id = CONTAINER_ID;
    document.body.appendChild(root);
  }
  return root;
}

function escapeHtml(text: string): string {
  const div = document.createElement("div");
  div.textContent = text;
  return div.innerHTML;
}

/**
 * 한 번에 모달 하나만 — 현재 열린 모달을 "취소"로 닫는 함수.
 * 새 모달 open 시 dismissActiveModal() 로 기존 것을 정리한 뒤 자신을 등록한다.
 */
let currentDismiss: (() => void) | null = null;

function dismissActiveModal(): void {
  const dismiss = currentDismiss;
  currentDismiss = null;
  if (dismiss) dismiss();
}

/** 알림 한 개만 띄우고 확인 버튼으로 닫기. opts.onOk 지정 시 확인 버튼 클릭에만 호출됨. */
export function alert(
  message: string,
  title?: string,
  opts?: { onOk?: () => void; variant?: "default" | "success" | "error" },
): Promise<void> {
  const defaultTitle = getModalLabel("alert_title");
  const okLabel = getModalLabel("ok");
  const closeLabel = getModalLabel("close");
  const titleText = title ?? defaultTitle;

  const root = getContainer();
  const backdrop = document.createElement("div");
  backdrop.className = BACKDROP_CLASS;

  const box = document.createElement("div");
  const variant = opts?.variant ?? "default";
  box.className = `${BOX_CLASS}${variant === "default" ? "" : ` ${BOX_CLASS}--${variant}`}`;
  const statusIcon = variant === "success"
    ? '<span class="fa-solid fa-check" aria-hidden="true"></span>'
    : variant === "error"
      ? '<span class="fa-solid fa-circle-exclamation" aria-hidden="true"></span>'
      : "";
  box.innerHTML = `
    <div class="${HEADER_CLASS}">
      ${statusIcon ? `<span class="${STATUS_CLASS}" aria-hidden="true">${statusIcon}</span>` : ""}
      <h2 class="${TITLE_CLASS}">${escapeHtml(titleText)}</h2>
      <button type="button" class="${CLOSE_CLASS}" data-app-modal-close aria-label="${escapeHtml(closeLabel)}"><i class="fa-solid fa-xmark" aria-hidden="true"></i></button>
    </div>
    <div class="${BODY_CLASS}">${escapeHtml(message)}</div>
    <div class="${FOOTER_CLASS}">
      <button type="button" class="${BTN_CLASS} ${BTN_PRIMARY_CLASS}" data-app-modal-ok>${escapeHtml(okLabel)}</button>
    </div>
  `;

  backdrop.appendChild(box);

  return new Promise((resolve) => {
    let settled = false;
    const finish = (runOk: boolean): void => {
      if (settled) return;
      settled = true;
      if (currentDismiss === dismiss) currentDismiss = null;
      backdrop.remove();
      resolve();
      if (runOk) opts?.onOk?.();
    };
    const dismiss = (): void => finish(false); // 다른 모달이 열리면 자동 닫힘(onOk 미실행)
    dismissActiveModal();        // 기존 모달 닫고(중첩 방지)
    currentDismiss = dismiss;
    root.appendChild(backdrop);  // 그 다음 이 모달만 표시
    box.querySelector("[data-app-modal-ok]")?.addEventListener("click", () => finish(true));
    box.querySelector("[data-app-modal-close]")?.addEventListener("click", () => finish(false));
    backdrop.addEventListener("click", (e) => {
      if (e.target === backdrop) finish(false);
    });
    box.addEventListener("click", (e) => e.stopPropagation());
  });
}

/** 확인/취소 선택, 확인 true / 취소 false */
export function confirm(message: string, title?: string): Promise<boolean> {
  const defaultTitle = getModalLabel("confirm_title");
  const okLabel = getModalLabel("ok");
  const cancelLabel = getModalLabel("cancel");
  const closeLabel = getModalLabel("close");
  const titleText = title ?? defaultTitle;

  const root = getContainer();
  const backdrop = document.createElement("div");
  backdrop.className = BACKDROP_CLASS;

  const box = document.createElement("div");
  box.className = BOX_CLASS;
  box.innerHTML = `
    <div class="${HEADER_CLASS}">
      <h2 class="${TITLE_CLASS}">${escapeHtml(titleText)}</h2>
      <button type="button" class="${CLOSE_CLASS}" data-app-modal-close aria-label="${escapeHtml(closeLabel)}"><i class="fa-solid fa-xmark" aria-hidden="true"></i></button>
    </div>
    <div class="${BODY_CLASS}">${escapeHtml(message)}</div>
    <div class="${FOOTER_CLASS}">
      <button type="button" class="${BTN_CLASS}" data-app-modal-cancel>${escapeHtml(cancelLabel)}</button>
      <button type="button" class="${BTN_CLASS} ${BTN_PRIMARY_CLASS}" data-app-modal-ok>${escapeHtml(okLabel)}</button>
    </div>
  `;

  backdrop.appendChild(box);

  return new Promise((resolve) => {
    let settled = false;
    const finish = (value: boolean): void => {
      if (settled) return;
      settled = true;
      if (currentDismiss === dismiss) currentDismiss = null;
      backdrop.remove();
      resolve(value);
    };
    const dismiss = (): void => finish(false); // 취소 = false (다른 모달이 열리면 자동 취소)
    dismissActiveModal();        // 기존 모달 닫고(중첩 방지)
    currentDismiss = dismiss;
    root.appendChild(backdrop);  // 그 다음 이 모달만 표시
    box.querySelector("[data-app-modal-ok]")?.addEventListener("click", () => finish(true));
    box.querySelector("[data-app-modal-cancel]")?.addEventListener("click", () => finish(false));
    box.querySelector("[data-app-modal-close]")?.addEventListener("click", () => finish(false));
    backdrop.addEventListener("click", (e) => {
      if (e.target === backdrop) finish(false);
    });
    box.addEventListener("click", (e) => e.stopPropagation());
  });
}

/**
 * HTML 내용을 스크롤 가능한 모달로 표시.
 * html 인자는 서버에서 렌더된 신뢰할 수 있는 HTML만 전달한다 (XSS 주의).
 */
export function content(html: string, title?: string, size: "wide" | "md" = "wide"): Promise<void> {
  const defaultTitle = getModalLabel("content_title");
  const okLabel = getModalLabel("ok");
  const closeLabel = getModalLabel("close");
  const titleText = title ?? defaultTitle;

  const root = getContainer();
  const backdrop = document.createElement("div");
  backdrop.className = BACKDROP_CLASS;

  const box = document.createElement("div");
  const sizeClass = size === "md" ? "app-modal-box--md" : "app-modal-box--wide";
  box.className = `${BOX_CLASS} ${sizeClass}`;
  box.innerHTML = `
    <div class="${HEADER_CLASS}">
      <h2 class="${TITLE_CLASS}">${escapeHtml(titleText)}</h2>
      <button type="button" class="${CLOSE_CLASS}" data-app-modal-close aria-label="${escapeHtml(closeLabel)}"><i class="fa-solid fa-xmark" aria-hidden="true"></i></button>
    </div>
    <div class="${BODY_CLASS} app-modal-body--scroll app-modal-prose" data-lenis-prevent></div>
    <div class="${FOOTER_CLASS}">
      <button type="button" class="${BTN_CLASS} ${BTN_PRIMARY_CLASS}" data-app-modal-ok>${escapeHtml(okLabel)}</button>
    </div>
  `;

  // HTML 내용은 innerHTML로 직접 주입 (신뢰 소스 전용)
  const bodyEl = box.querySelector<HTMLElement>(`.${BODY_CLASS}`);
  if (bodyEl) bodyEl.innerHTML = html;

  backdrop.appendChild(box);

  return new Promise((resolve) => {
    let settled = false;
    const finish = (): void => {
      if (settled) return;
      settled = true;
      if (currentDismiss === dismiss) currentDismiss = null;
      backdrop.remove();
      resolve();
    };
    const dismiss = (): void => finish();
    dismissActiveModal();        // 기존 모달 닫고(중첩 방지)
    currentDismiss = dismiss;
    root.appendChild(backdrop);  // 그 다음 이 모달만 표시
    box.querySelector("[data-app-modal-ok]")?.addEventListener("click", () => finish());
    box.querySelector("[data-app-modal-close]")?.addEventListener("click", () => finish());
    backdrop.addEventListener("click", (e) => {
      if (e.target === backdrop) finish();
    });
    box.addEventListener("click", (e) => e.stopPropagation());
  });
}

export const Modal = { alert, confirm, content };
